import { zodResolver } from '@hookform/resolvers/zod';
import { PropsWithChildren, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useDragAndDropEditorContext } from 'modules/dragAndDropEditor/context';
import { ObjectKey } from 'utils/tsHelpers';
import { SavedRow } from '../../types';
import { getSavedRowFormInitialValues } from './CreateOrEditSavedRowSlideOver.utils';

export const CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES = {
  ROW_NAME: 'rowName',
  CATEGORY: 'category',
} as const;

const CreateOrEditSavedRowFormSchema = z.object({
  [CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.ROW_NAME]: z
    .string()
    .trim()
    .min(1, 'Row name is required to create row')
    .max(180, 'Row name must not exceed 180 characters'),
  [CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.CATEGORY]: z
    .object({
      label: z.string().trim().max(40, 'Category name must not exceed 40 characters').optional(), // category name
      value: z.string().optional(), // category id
    })
    .nullable(),
});

export type CreateOrEditSavedRowFormValues = Required<
  z.infer<typeof CreateOrEditSavedRowFormSchema>
>;
type CreateOrEditSavedRowFormProviderProps = PropsWithChildren<{
  savedRow?: SavedRow;
}>;

export const CreateOrEditSavedRowFormProvider = ({
  children,
  savedRow,
}: CreateOrEditSavedRowFormProviderProps) => {
  const initialValues = useMemo(() => getSavedRowFormInitialValues(savedRow), [savedRow]);

  const methods = useForm<CreateOrEditSavedRowFormValues>({
    defaultValues: initialValues,
    mode: 'onTouched',
    resolver: zodResolver(CreateOrEditSavedRowFormSchema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};

type WithCreateOrEditSavedRowFormProviderProps<Props> = Props & {
  /** The saved row being edited's ID */
  savedRowId?: number;
};

export const withCreateOrEditSavedRowFormProvider = <
  ComponentProps extends Record<ObjectKey, unknown>,
>(
  Component: React.ComponentType<WithCreateOrEditSavedRowFormProviderProps<ComponentProps>>,
) => {
  return (props: WithCreateOrEditSavedRowFormProviderProps<ComponentProps>) => {
    const { savedRows } = useDragAndDropEditorContext();
    const { savedRowId } = props;

    const savedRow = useMemo(() => {
      return savedRows?.find?.((row: SavedRow) => {
        const rowId = row?.id;
        return rowId && savedRowId && rowId?.toString() === savedRowId?.toString();
      });
    }, [savedRows, savedRowId]);

    return (
      <CreateOrEditSavedRowFormProvider savedRow={savedRow}>
        <Component savedRow={savedRow} {...props} />
      </CreateOrEditSavedRowFormProvider>
    );
  };
};
